import { MatterTemplateDictionary } from "@/components/features/settings/defaults/matter-template-dictionary/MatterTemplateDictionary";
import {
  Button,
  Dialog,
  DialogContent,
  DialogTrigger,
  Typography,
} from "@/components/ui";
import { cn } from "@/lib/utils/cn";
import type { ColumnFiltersState } from "@tanstack/react-table";
import toast from "react-hot-toast";
import { type Ref, useCallback, useState } from "react";

type TemplateFieldParent = "signer" | "userSigner";

interface TemplateFieldsPanelContentProps {
  onInsertAtCursor?: (value: string) => Promise<boolean>;
  allowedParents?: readonly TemplateFieldParent[];
  bodyClassName?: string;
  cardGridClassName?: string;
  toolbarClassName?: string;
  searchInputClassName?: string;
  controlsClassName?: string;
  collapseButtonClassName?: string;
}

export const TemplateFieldsPanelContent = ({
  onInsertAtCursor,
  allowedParents,
  bodyClassName,
  cardGridClassName,
  toolbarClassName,
  searchInputClassName,
  controlsClassName,
  collapseButtonClassName,
}: TemplateFieldsPanelContentProps) => {
  const [globalFilter, setGlobalFilter] = useState("");
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [openSections, setOpenSections] = useState<string[] | undefined>();

  const handleInsert = useCallback(
    async (value: string) => {
      if (!onInsertAtCursor) return;
      const inserted = await onInsertAtCursor(value);
      if (inserted) {
        toast.success("Inserted at cursor.");
        return;
      }
      try {
        await navigator.clipboard.writeText(value);
        toast.success("Copied to clipboard.");
      } catch {
        toast.error("Unable to insert or copy.");
      }
    },
    [onInsertAtCursor],
  );

  return (
    <>
      <div className="border-b px-5 py-4">
        <Typography variant="heading" size="sm">
          Template fields
        </Typography>
        <Typography variant="default" className="text-muted-foreground text-sm">
          Use tags like{" "}
          <span className="bg-muted rounded px-1 font-mono text-xs">
            {"{{clientName}}"}
          </span>{" "}
          inside your DOCX.
        </Typography>
        {onInsertAtCursor && (
          <Typography
            variant="default"
            className="text-muted-foreground text-xs"
          >
            Insert places the tag at the current cursor. If it fails, the tag is
            copied instead.
          </Typography>
        )}
      </div>
      <div
        className={cn(
          "max-h-[calc(85vh-88px)] overflow-y-auto px-5 py-4",
          bodyClassName,
        )}
      >
        <MatterTemplateDictionary
          onVariableSelect={onInsertAtCursor ? handleInsert : undefined}
          selectLabel="Insert"
          allowedParents={allowedParents}
          cardGridClassName={
            cardGridClassName ??
            "grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-2"
          }
          toolbarClassName={toolbarClassName}
          searchInputClassName={searchInputClassName}
          controlsClassName={controlsClassName}
          collapseButtonClassName={collapseButtonClassName}
          globalFilter={globalFilter}
          onGlobalFilterChange={setGlobalFilter}
          columnFilters={columnFilters}
          onColumnFiltersChange={setColumnFilters}
          openSections={openSections}
          onOpenSectionsChange={setOpenSections}
        />
      </div>
    </>
  );
};

interface TemplateFieldsDialogProps {
  onInsertAtCursor?: (value: string) => Promise<boolean>;
  triggerLabel?: string;
  triggerRef?: Ref<HTMLButtonElement>;
  /** Hide the trigger button (e.g. when opened from the in-editor Apryse control via `triggerRef`). */
  hideTrigger?: boolean;
  allowedParents?: readonly TemplateFieldParent[];
}

export const TemplateFieldsDialog = ({
  onInsertAtCursor,
  triggerLabel = "Template fields",
  triggerRef,
  hideTrigger = false,
  allowedParents,
}: TemplateFieldsDialogProps) => {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button
          ref={triggerRef}
          type="button"
          variant="outline"
          size="sm"
          className={cn(hideTrigger && "sr-only")}
        >
          {triggerLabel}
        </Button>
      </DialogTrigger>
      <DialogContent
        className="z-[120] max-h-[85vh] w-[min(95vw,900px)] max-w-[95vw] overflow-hidden p-0"
        aria-describedby={undefined}
      >
        <TemplateFieldsPanelContent
          onInsertAtCursor={onInsertAtCursor}
          allowedParents={allowedParents}
        />
      </DialogContent>
    </Dialog>
  );
};
